/**
 * Шумы и расстояния — сырьё для полей.
 *
 * Всё здесь работает с `Float32Array` длиной w*h и индексом `y*w + x`.
 * Ни одна функция не знает про canvas, DOM и время: это чистая арифметика,
 * которую можно проверить тестом на числах.
 */

import type { Rng } from './rng'

/**
 * Одна октава value-noise: решётка случайных значений, сглаженная
 * по smoothstep.
 *
 * Не Перлин и не симплекс намеренно. Value-noise стоит примерно вчетверо
 * дешевле, а после порога разница между ними на глаз не читается: мы берём
 * не сам шум, а его подуровневое множество. Тратить на градиентный шум
 * втрое больше времени, чтобы затем всё равно его срезать порогом, — плата
 * за красоту промежуточного результата, которого никто не увидит.
 */
export function valueNoise(w: number, h: number, cells: number, rnd: Rng): Float32Array {
  const gx = Math.max(2, cells) + 1
  const gy = Math.max(2, Math.round((cells * h) / w)) + 1
  const grid = new Float32Array(gx * gy)
  for (let i = 0; i < grid.length; i++) grid[i] = rnd()

  const out = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    const fy = (y / Math.max(1, h - 1)) * (gy - 1)
    const y0 = Math.min(gy - 2, Math.floor(fy))
    const ty = fy - y0
    const sy = ty * ty * (3 - 2 * ty)

    for (let x = 0; x < w; x++) {
      const fx = (x / Math.max(1, w - 1)) * (gx - 1)
      const x0 = Math.min(gx - 2, Math.floor(fx))
      const tx = fx - x0
      const sx = tx * tx * (3 - 2 * tx)

      const a = grid[y0 * gx + x0]!
      const b = grid[y0 * gx + x0 + 1]!
      const c = grid[(y0 + 1) * gx + x0]!
      const d = grid[(y0 + 1) * gx + x0 + 1]!

      out[y * w + x] = (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy
    }
  }
  return out
}

/**
 * Фрактальный шум: сумма октав с затуханием.
 *
 * `gain` — не украшение, а характер. 0.5 даёт мягкие облака; 0.71 —
 * заметно грубее и соответствует фронту медленного горения бумаги
 * (класс KPZ, показатель шероховатости ½). Отсюда `burn` берёт 0.71,
 * а спокойные поля — 0.5.
 */
export function fbm(
  w: number,
  h: number,
  rnd: Rng,
  opts: { octaves?: number; cells?: number; gain?: number; lacunarity?: number } = {},
): Float32Array {
  const octaves = opts.octaves ?? 3
  const lacunarity = opts.lacunarity ?? 2.4
  const gain = opts.gain ?? 0.5

  const out = new Float32Array(w * h)
  let cells = opts.cells ?? 3
  let amp = 1

  for (let o = 0; o < octaves; o++) {
    const layer = valueNoise(w, h, Math.round(cells), rnd)
    for (let i = 0; i < out.length; i++) out[i]! += layer[i]! * amp
    cells *= lacunarity
    amp *= gain
  }
  return normalize(out)
}

/** Растянуть значения в 0…1. Без этого пороги пришлось бы подбирать на глаз. */
export function normalize(f: Float32Array): Float32Array {
  let min = Infinity
  let max = -Infinity
  for (let i = 0; i < f.length; i++) {
    const v = f[i]!
    if (v < min) min = v
    if (v > max) max = v
  }
  const d = max - min || 1
  for (let i = 0; i < f.length; i++) f[i] = (f[i]! - min) / d
  return f
}

/** Смешать два поля. Возвращает первое, изменённое на месте. */
export function mix(a: Float32Array, b: Float32Array, k: number): Float32Array {
  for (let i = 0; i < a.length; i++) a[i] = a[i]! * (1 - k) + b[i]! * k
  return a
}

/**
 * Медиана 3×3.
 *
 * Ранговые фильтры коммутируют с порогом: медиана по полю — это медиана
 * по всем кадрам сразу, и вложенность кадров при этом остаётся. Сглаживать
 * сами кадры было бы нельзя — так пиксель может залиться и разлиться
 * обратно, и переход поедет назад.
 */
export function median3(f: Float32Array, w: number, h: number): Float32Array {
  const out = new Float32Array(f.length)
  const win = new Float32Array(9)

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let n = 0
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          if (xx < 0 || xx >= w) continue
          win[n++] = f[yy * w + xx]!
        }
      }
      const slice = Array.prototype.slice.call(win, 0, n) as number[]
      slice.sort((p, q) => p - q)
      out[y * w + x] = slice[n >> 1]!
    }
  }
  return out
}

/**
 * Разделимое box-размытие.
 *
 * Нужно перед порогом, а не после: маска печётся при разрешении меньше
 * экранного и потом увеличивается браузером. Если порог взять с резкого
 * поля, увеличение раздувает ступеньку в блок из нескольких пикселей —
 * то самое «дробление», которое видно на буквах. Сглаженное поле даёт
 * плавную границу, и увеличение её не портит.
 *
 * Разделимое: два прохода по одной оси вместо квадрата ядра. Для радиуса 2
 * это 10 сложений на пиксель вместо 25.
 */
export function boxBlur(f: Float32Array, w: number, h: number, radius: number): Float32Array {
  if (radius < 1) return f
  const tmp = new Float32Array(f.length)
  const out = new Float32Array(f.length)
  const span = radius * 2 + 1

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0
      for (let k = -radius; k <= radius; k++) {
        const xx = Math.min(w - 1, Math.max(0, x + k))
        sum += f[y * w + xx]!
      }
      tmp[y * w + x] = sum / span
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0
      for (let k = -radius; k <= radius; k++) {
        const yy = Math.min(h - 1, Math.max(0, y + k))
        sum += tmp[yy * w + x]!
      }
      out[y * w + x] = sum / span
    }
  }
  return out
}

/** Точка на поле, в единицах 0…1 по обеим осям. */
export interface Point {
  x: number
  y: number
}

/** Разбросать точки со сглаживанием к равномерности. */
export function scatter(count: number, rnd: Rng, margin = 0.08): Point[] {
  const pts: Point[] = []
  for (let i = 0; i < count; i++) {
    // Лучший из трёх бросков по удалённости от уже поставленных: дешёвая
    // замена честной выборке по синему шуму. Скопления точек читаются
    // как ошибка, а равномерная решётка — как узор; нужна середина.
    let best: Point | null = null
    let bestDist = -1
    for (let attempt = 0; attempt < 3; attempt++) {
      const p = {
        x: margin + rnd() * (1 - 2 * margin),
        y: margin + rnd() * (1 - 2 * margin),
      }
      let d = Infinity
      for (const q of pts) d = Math.min(d, (p.x - q.x) ** 2 + (p.y - q.y) ** 2)
      if (d > bestDist) {
        bestDist = d
        best = p
      }
    }
    pts.push(best!)
  }
  return pts
}
